import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { CSSProperties, useEffect } from 'react';
import palette from '@ui/palette';
import { CommentData } from '../types';
import CommentSkeleton from './CommentSkeleton';
import CommentDescription from './CommentDescription';

interface CommentFeedProps {
  comments: CommentData[];
  loading?: boolean;
  clipId: string;
  renderComment: (props: { item: CommentData; index: number }) => JSX.Element;
}

const CommentFeed = (props: CommentFeedProps) => {
  const { comments, renderComment, loading = false, clipId } = props;
  const { classes } = useStyles();
  const listRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight });
    }
  }, [clipId]);

  return (
    <div
      ref={listRef}
      id="wsl-comment-scroll"
      className={classes.scrollContainer}
    >
      <div role="feed" aria-busy={loading} className={classes.container}>
        {comments.map((comment, index) =>
          renderComment({ item: comment, index })
        )}
      </div>
    </div>
  );
};

const CommentFeedWrapper = (props: CommentFeedProps) => {
  const { comments, renderComment, loading = false, clipId } = props;
  const { classes, cx } = useStyles();
  if (loading)
    return (
      <div className={cx(classes.sectionContainer, classes.skeleton)}>
        {Array(10)
          .fill(0)
          .map(() => (
            <CommentSkeleton />
          ))}
      </div>
    );

  if (!loading && comments.length === 0)
    return (
      <div className={cx(classes.sectionContainer, classes.withBorderTop)}>
        <CommentDescription />
      </div>
    );

  return (
    <CommentFeed
      comments={comments}
      renderComment={renderComment}
      /**
       * @TODO should reflect loadMore from pagination
       */
      loading={false}
      clipId={clipId}
    />
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  scrollContainer: {
    height: '100%',
    borderTop: `1px solid ${palette.gray_800}`,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...(theme.mixins.scrollBar as CSSProperties),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 0.75, 1.5, 1.25),
    boxSizing: 'border-box',
  },
  skeleton: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'hidden',
  },
  sectionContainer: {
    display: 'flex',
    padding: theme.spacing(2, 1.5),
  },
  withBorderTop: {
    borderTop: `1px solid ${palette.gray_800}`,
  },
}));

export default React.memo(CommentFeedWrapper);
